import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { BookOpen, FileText, ClipboardList, Bell, Users, ArrowRight, Trophy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StudentDashboard = () => {
  const { token, user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [notices, setNotices] = useState([]);
  const [tests, setTests] = useState([]);
  const [referralStats, setReferralStats] = useState({ total_referrals: 0, successful_referrals: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [enrollmentsRes, noticesRes, testsRes, referralsRes] = await Promise.all([
          axios.get(`${API_URL}/api/enrollments/my`, { headers }),
          axios.get(`${API_URL}/api/notices`),
          axios.get(`${API_URL}/api/test-series`),
          axios.get(`${API_URL}/api/referrals/stats`, { headers })
        ]);
        setEnrollments(enrollmentsRes.data);
        setNotices(noticesRes.data.slice(0, 5));
        setTests(testsRes.data.slice(0, 3));
        setReferralStats(referralsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const stats = [
    { title: 'Enrolled Courses', value: enrollments.length, icon: BookOpen, color: 'bg-primary' },
    { title: 'Upcoming Tests', value: tests.length, icon: ClipboardList, color: 'bg-green-500' },
    { title: 'New Notices', value: notices.length, icon: Bell, color: 'bg-yellow-500' },
    { title: 'Referrals', value: referralStats.total_referrals, icon: Users, color: 'bg-purple-500' },
  ];

  return (
    <DashboardLayout title="Dashboard">
      {/* Welcome Banner */}
      <Card className="mb-6 bg-gradient-to-r from-primary to-accent text-white border-0" data-testid="welcome-card">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="font-heading text-2xl font-bold mb-1">Welcome back, {user?.name?.split(' ')[0]}!</h2>
              <p className="text-white/80">Continue your learning journey and achieve your goals.</p>
            </div>
            <Button variant="secondary" asChild data-testid="explore-courses-btn">
              <Link to="/courses">
                Explore Courses
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {stats.map((stat, index) => (
          <Card key={stat.title} data-testid={`stat-card-${index}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-sm text-muted-foreground">{stat.title}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Enrolled Courses */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-heading font-semibold text-lg">My Courses</h3>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/dashboard/courses">View All</Link>
            </Button>
          </div>

          {loading ? (
            <Card className="animate-pulse">
              <CardContent className="h-32"></CardContent>
            </Card>
          ) : enrollments.length > 0 ? (
            enrollments.slice(0, 3).map((enrollment) => (
              <Card key={enrollment.id} className="card-hover" data-testid={`enrollment-${enrollment.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="w-full md:w-24 h-20 rounded-lg bg-primary/10 flex items-center justify-center">
                      <BookOpen className="w-10 h-10 text-primary/50" />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-semibold">{enrollment.course?.name || 'Course'}</h4>
                          <Badge variant="secondary" className="mt-1">{enrollment.course?.category}</Badge>
                        </div>
                        <Badge className={enrollment.status === 'active' ? 'bg-green-100 text-green-700' : ''}>
                          {enrollment.status}
                        </Badge>
                      </div>
                      <div className="mt-3">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-muted-foreground">Progress</span>
                          <span className="font-medium">{enrollment.progress || 0}%</span>
                        </div>
                        <Progress value={enrollment.progress || 0} className="h-2" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                <h4 className="font-semibold mb-2">No Courses Yet</h4>
                <p className="text-muted-foreground text-sm mb-4">Start your journey by enrolling in a course.</p>
                <Button asChild>
                  <Link to="/courses">Browse Courses</Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Upcoming Tests */}
          <Card data-testid="upcoming-tests-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardList className="w-5 h-5" />
                Upcoming Tests
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tests.length > 0 ? (
                <div className="space-y-3">
                  {tests.map((test) => (
                    <div key={test.id} className="flex justify-between items-center p-3 bg-muted rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{test.title}</p>
                        <p className="text-xs text-muted-foreground">{test.scheduled_date}</p>
                      </div>
                      <Badge variant="outline">{test.duration_minutes} min</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No upcoming tests</p>
              )}
              <Button variant="ghost" className="w-full mt-3" asChild>
                <Link to="/dashboard/tests">View All Tests</Link>
              </Button>
            </CardContent>
          </Card>

          {/* Recent Notices */}
          <Card data-testid="notices-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Notices
              </CardTitle>
            </CardHeader>
            <CardContent>
              {notices.length > 0 ? (
                <div className="space-y-3">
                  {notices.slice(0, 3).map((notice) => (
                    <div key={notice.id} className="p-3 bg-muted rounded-lg">
                      <div className="flex items-start gap-2">
                        {notice.is_important && <Badge variant="destructive" className="text-xs">Important</Badge>}
                        <p className="font-medium text-sm line-clamp-2">{notice.title}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No new notices</p>
              )}
              <Button variant="ghost" className="w-full mt-3" asChild>
                <Link to="/dashboard/notices">View All Notices</Link>
              </Button>
            </CardContent>
          </Card>

          {/* Referral Card */}
          <Card className="bg-gradient-to-br from-secondary/20 to-secondary/5 border-secondary/30" data-testid="referral-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-secondary-foreground" />
                </div>
                <div>
                  <p className="font-semibold">Refer & Earn</p>
                  <p className="text-xs text-muted-foreground">Get rewards for referrals</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Your referral code: <span className="font-mono font-bold text-foreground">{user?.referral_code || 'KOTA1234'}</span>
              </p>
              <Button variant="secondary" className="w-full" asChild>
                <Link to="/dashboard/referrals">View Referrals</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default StudentDashboard;
